import React, { useState, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { ArrowRight, CheckCircle2 } from 'lucide-react'
import api from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import { AuthShell, EASE_OUT_STRONG } from '@/components/auth/AuthShell'

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const { logout } = useAuth()

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isVerifying, setIsVerifying] = useState(true)
  const [tokenValid, setTokenValid] = useState(false)
  const [userEmail, setUserEmail] = useState('')
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('No reset token provided')
        setIsVerifying(false)
        return
      }

      // If a session exists, tear it down before showing the form so a
      // reset link for one user can never inherit another user's session.
      // We gate on localStorage — NOT useAuth's `isAuthenticated` — because
      // useAuth's init effect races with this component's mount and can
      // read `false` here even when a token is sitting in localStorage.
      // That race let an admin who'd just approved an affiliate from the
      // same browser carry their session through into the new affiliate's
      // password setup, landing them on the admin's dashboard afterward.
      if (localStorage.getItem('app_auth_token')) {
        try { await logout() } catch { /* server-side may have rejected; client-side cleanup already ran */ }
      }

      try {
        const response = await api.get(`/api/auth/reset-password/${token}`)
        const data = response.data

        if (data.valid) {
          setTokenValid(true)
          setUserEmail(data.email)
        } else {
          setError(data.error || 'Invalid or expired reset link')
        }
      } catch {
        setError('Failed to verify reset link')
      } finally {
        setIsVerifying(false)
      }
    }

    verifyToken()
    // We intentionally only re-run on token change — isAuthenticated/logout
    // shouldn't trigger a re-verify.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  // Match <html>/<body> background to the shell so iOS overscroll doesn't flash
  useEffect(() => {
    const html = document.documentElement
    const body = document.body
    const prevHtml = html.style.backgroundColor
    const prevBody = body.style.backgroundColor
    html.style.backgroundColor = '#0d0a1f'
    body.style.backgroundColor = '#0d0a1f'
    return () => {
      html.style.backgroundColor = prevHtml
      body.style.backgroundColor = prevBody
    }
  }, [])

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) return 'Password must be at least 8 characters'
    if (!/[A-Z]/.test(pwd)) return 'Password must contain at least one uppercase letter'
    if (!/[a-z]/.test(pwd)) return 'Password must contain at least one lowercase letter'
    if (!/[0-9]/.test(pwd)) return 'Password must contain at least one number'
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    const passwordError = validatePassword(password)
    if (passwordError) {
      setError(passwordError)
      return
    }

    setIsLoading(true)

    try {
      const response = await api.post(`/api/auth/reset-password`, { token, password })
      const data = response.data

      if (data.success !== false) {
        setSuccess(true)
        // Hard reload into /login so useAuth re-initializes from the
        // (now-empty) localStorage. Without this, navigate('/login')
        // would hit the authenticated routes block (the React state is
        // still stale from initial mount) and bounce back to /.
        // Belt-and-braces: scrub auth storage one more time right before
        // the redirect. The mount-time logout should have already done
        // this, but if anything ever leaks through, this guarantees the
        // /login landing is on a clean session and not someone else's.
        try {
          localStorage.removeItem('app_auth_token')
          localStorage.removeItem('app_agent_data')
          localStorage.removeItem('app_onboarding_complete')
          localStorage.removeItem('app_impersonation')
        } catch { /* storage quota / private mode — ignore */ }
        setTimeout(() => { window.location.href = '/login' }, 2500)
      } else {
        setError(data.error || 'Failed to reset password')
      }
    } catch {
      setError('Failed to connect to server')
    } finally {
      setIsLoading(false)
    }
  }

  // ==========================================================================
  //   VERIFYING (loading)
  // ==========================================================================
  if (isVerifying) {
    return (
      <AuthShell
        headline={<>Let's get you<br />back in.</>}
        sub="Verifying your password reset link..."
      >
        <div className="flex items-center gap-3 text-white/60">
          <LoadingSpinner size="sm" />
          <p className="text-sm">Checking the link…</p>
        </div>
      </AuthShell>
    )
  }

  // ==========================================================================
  //   INVALID TOKEN
  // ==========================================================================
  if (!tokenValid) {
    return (
      <AuthShell
        headline={<>This link<br />has expired.</>}
        sub="Setup and password reset links expire after their security window. Request a fresh one to continue."
      >
        <div
          className="opacity-0 animate-fade-in-up"
          style={{ animationDelay: '80ms', animationFillMode: 'forwards', animationTimingFunction: EASE_OUT_STRONG }}
        >
          <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight mb-2">Link expired</h2>
          <p className="text-white/60 text-sm mb-6">
            {error || 'This reset link is no longer valid.'}
          </p>

          <Link
            to="/login"
            className="inline-flex items-center gap-2 h-12 px-5 rounded-xl text-[15px] font-semibold text-white bg-gradient-to-r from-brand-royal to-brand-periwinkle hover:brightness-110 shadow-lg shadow-brand-royal/30 hover:shadow-brand-royal/50 transition-[transform,box-shadow,filter] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97]"
          >
            Request a new link
            <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </Link>
        </div>
      </AuthShell>
    )
  }

  // ==========================================================================
  //   SUCCESS
  // ==========================================================================
  if (success) {
    return (
      <AuthShell
        headline={<>You're all<br />set.</>}
        sub="Your new password is saved. Redirecting you to sign in…"
      >
        <div
          className="opacity-0 animate-fade-in-up"
          style={{ animationDelay: '80ms', animationFillMode: 'forwards', animationTimingFunction: EASE_OUT_STRONG }}
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-500/10 px-3 py-1.5 text-xs font-medium text-emerald-300 mb-4">
            <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2} />
            Password updated
          </div>
          <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight mb-2">
            Password saved.
          </h2>
          <p className="text-white/60 text-sm mb-6">
            Taking you to sign in in a moment.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-brand-periwinkle hover:text-white text-sm font-medium transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.98]"
          >
            Go to sign in now
            <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </Link>
        </div>
      </AuthShell>
    )
  }

  // ==========================================================================
  //   FORM
  // ==========================================================================
  return (
    <AuthShell
      headline={<>Set a new<br />password.</>}
      sub="Make it something you'll actually remember. 8 characters with a mix of upper, lower, and a number."
    >
      <div
        className="opacity-0 animate-fade-in-up"
        style={{ animationDelay: '80ms', animationFillMode: 'forwards', animationTimingFunction: EASE_OUT_STRONG }}
      >
        <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight mb-2">New password</h2>
        <p className="text-white/60 text-sm mb-6 break-all">
          For <span className="text-white/90">{userEmail}</span>
        </p>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <fieldset disabled={isLoading} className="space-y-4 border-0 p-0 m-0 min-w-0">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white/75 mb-2">
                New password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                autoFocus
                className="block w-full h-12 px-4 border border-white/10 rounded-xl placeholder-white/30 text-white bg-white/[0.04] focus:outline-none focus:ring-2 focus:ring-brand-royal/70 focus:border-transparent text-[15px] transition-[border-color,box-shadow,background-color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)]"
                placeholder="Enter a new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <p className="mt-1.5 text-xs text-white/40">
                Minimum 8 characters, including uppercase, lowercase, and a number
              </p>
            </div>

            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-white/75 mb-2">
                Confirm password
              </label>
              <input
                id="confirm-password"
                name="confirm-password"
                type="password"
                autoComplete="new-password"
                required
                className="block w-full h-12 px-4 border border-white/10 rounded-xl placeholder-white/30 text-white bg-white/[0.04] focus:outline-none focus:ring-2 focus:ring-brand-royal/70 focus:border-transparent text-[15px] transition-[border-color,box-shadow,background-color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)]"
                placeholder="Type it again"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-400/20 rounded-xl p-3 animate-in fade-in slide-in-from-top-1 duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]">
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center items-center h-12 px-4 text-[15px] font-semibold rounded-xl text-white bg-gradient-to-r from-brand-royal to-brand-periwinkle hover:brightness-110 shadow-lg shadow-brand-royal/30 hover:shadow-brand-royal/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#0d0a1f] focus:ring-brand-royal disabled:opacity-50 disabled:cursor-not-allowed transition-[transform,box-shadow,filter] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] disabled:active:scale-100 border-0"
            >
              {isLoading ? <LoadingSpinner size="sm" /> : 'Save new password'}
            </button>

            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 py-2 -my-2 text-white/55 hover:text-white text-sm font-medium transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.98]"
            >
              <ArrowRight className="h-4 w-4 rotate-180" strokeWidth={2} />
              Back to sign in
            </Link>
          </fieldset>
        </form>
      </div>
    </AuthShell>
  )
}
